// src/features/analytics/components/EventWindowControl.tsx
//
// The stream report's window control (dashboard ADR-009, amended 2026-09-13).
// One compact control, three modes: Broadcast (a live run, padded by an hour
// each side), Day (one calendar day — today by default), Range (a start date
// to an end date, whole days unless hours are set; end exclusive, so the
// requested window IS the effective window). Writes `wfrom`/`wto` instants to
// the URL; clearing them returns to today. Under the control: the effective
// window in the viewer's zone, a copy-link action, "Back to today" whenever the
// window is not today, and a secondary way to widen to the Content tab's range. An always-on stream has no broadcasts to pick:
// the mode is hidden and one line says why. Full width on phones.

import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Anchor, Box, Button, Group, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconCheck, IconLink } from '@tabler/icons-react';
import {
  BROADCAST_PAD_SECONDS,
  HOURLY_MAX_RANGE_DAYS,
  STREAM_WINDOW_MODES,
  STREAM_WINDOW_MODE_LABELS,
  type StreamWindowMode,
} from '@/lib/constants/analytics';
import { DateField } from '@/shared/components/DateField';
import type { DayZone } from '@/shared/utils/utcRange';
import { CONTINUOUS_STREAM_COPY, broadcastMatchingWindow } from '../broadcasts';
import {
  dayNavState,
  dayStringOf,
  dayWindow,
  effectiveWindowLabel,
  hourOptions,
  inferWindowMode,
  isTodayWindow,
  rangeFieldsFor,
  rangeIsWholeDays,
  rangeWindow,
  shiftDay,
  type EventWindow,
  type RangeFields,
} from '../eventWindow';
import { BroadcastPicker } from './BroadcastPicker';
import type { BroadcastRow } from '../types';

export interface BroadcastListState {
  rows: BroadcastRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  emptyCopy: string;
  /** An always-on stream: hide the Broadcast mode and say why. */
  continuous: boolean;
}

interface EventWindowControlProps {
  /** The window in effect — the URL's explicit window or the default (today), already floored. */
  value: EventWindow;
  /** True when the URL carries an explicit `wfrom`/`wto`. */
  explicit: boolean;
  /** Write a window to the URL; null clears it (back to today). */
  onChange: (window: EventWindow | null) => void;
  zone: DayZone;
  /** Hour-stable "now" (the end of the current hour). */
  nowSeconds: number;
  floorSeconds: number | null;
  /** The Content tab's date range as a window, when it can be windowed (≤ 92 days). */
  pageRange: EventWindow | null;
  pageRangeLabel: string;
  /** VOD items have no broadcasts; the Broadcast mode is hidden for them. */
  showBroadcasts: boolean;
  broadcasts: BroadcastListState;
}

const COPIED_MS = 1500;

/** Which modes the control offers: no Broadcast for VOD or an always-on stream. */
export function availableWindowModes(showBroadcasts: boolean, continuous: boolean): StreamWindowMode[] {
  return STREAM_WINDOW_MODES.filter((m) => m !== 'broadcast' || (showBroadcasts && !continuous));
}

export function EventWindowControl({
  value,
  explicit,
  onChange,
  zone,
  nowSeconds,
  floorSeconds,
  pageRange,
  pageRangeLabel,
  showBroadcasts,
  broadcasts,
}: EventWindowControlProps) {
  const modes = availableWindowModes(showBroadcasts, broadcasts.continuous);
  const broadcastMode = modes.includes('broadcast');
  const matchedBroadcast =
    explicit && broadcastMode
      ? broadcastMatchingWindow(broadcasts.rows, value.from, value.to, BROADCAST_PAD_SECONDS)
      : null;
  const today = dayStringOf(nowSeconds, zone);
  const floorDay = floorSeconds !== null ? dayStringOf(floorSeconds, zone) : undefined;

  // Mode follows the window unless this control just wrote it (no jumps while typing).
  const lastEmitted = useRef<EventWindow | null>(null);
  const [mode, setMode] = useState<StreamWindowMode>(() =>
    inferWindowMode(value, nowSeconds, zone, matchedBroadcast !== null)
  );
  useEffect(() => {
    const own = lastEmitted.current && lastEmitted.current.from === value.from && lastEmitted.current.to === value.to;
    if (!own) setMode(inferWindowMode(value, nowSeconds, zone, matchedBroadcast !== null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.from, value.to, matchedBroadcast !== null]);
  // A mode that just became unavailable (the list resolved to "always on") falls back to Day.
  const effectiveMode: StreamWindowMode = modes.includes(mode) ? mode : 'day';

  const emit = (next: EventWindow | null) => {
    lastEmitted.current = next;
    onChange(next);
  };

  // Day mode.
  const day = dayStringOf(value.from, zone);
  const pickDay = (date: string | null) => {
    if (!date) return;
    if (date === today) {
      emit(null);
      return;
    }
    const w = dayWindow(date, nowSeconds, zone);
    if (w) emit(w);
  };

  // Range mode: local field state, committed only when it forms a valid window.
  const [range, setRange] = useState<RangeFields>(() => rangeFieldsFor(value, zone));
  const [rangeError, setRangeError] = useState<string | null>(null);
  useEffect(() => {
    setRange(rangeFieldsFor(value, zone));
    setRangeError(null);
  }, [value.from, value.to, zone]);
  const nav = dayNavState(day, today, floorDay);
  const commitRange = (next: RangeFields) => {
    setRange(next);
    const w = rangeWindow(next.startDate, next.endDate, zone, next.startHour, next.endHour);
    if (!w) {
      setRangeError(
        next.endDate < next.startDate || (next.endDate === next.startDate && next.endHour <= next.startHour)
          ? 'The end must be after the start.'
          : `Ranges can be up to ${HOURLY_MAX_RANGE_DAYS} days.`
      );
      return;
    }
    setRangeError(null);
    if (w.from !== value.from || w.to !== value.to) emit(w);
  };

  // Copy link.
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      // Clipboard unavailable (insecure context / permission): the URL bar still has the link.
    }
  };

  const pageRangeIsCurrent = pageRange !== null && pageRange.from === value.from && pageRange.to === value.to;
  // "Back to today" lives in the always-visible actions row, whatever the mode (one placement).
  const isToday = isTodayWindow(value, nowSeconds, zone);

  return (
    <Stack gap="xs" w="100%">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <SegmentedControl
          size="sm"
          aria-label="Window mode"
          data={modes.map((m) => ({ value: m, label: STREAM_WINDOW_MODE_LABELS[m] }))}
          value={effectiveMode}
          onChange={(m) => setMode(m as StreamWindowMode)}
          w={{ base: '100%', sm: 'auto' }}
        />

        {effectiveMode === 'broadcast' && (
          <BroadcastPicker
            rows={broadcasts.rows}
            isLoading={broadcasts.isLoading}
            isError={broadcasts.isError}
            onRetry={broadcasts.onRetry}
            emptyCopy={broadcasts.emptyCopy}
            value={explicit ? value : null}
            onPick={(w) => emit(w)}
            zone={zone}
            nowSeconds={nowSeconds}
          />
        )}

        {effectiveMode === 'day' && (
          <Group gap="xs" align="center" wrap="nowrap" w={{ base: '100%', sm: 'auto' }}>
            <ActionIcon
              variant="default"
              size="lg"
              aria-label="Previous day"
              disabled={!nav.canGoBack}
              onClick={() => pickDay(shiftDay(day, -1, zone))}
            >
              <IconChevronLeft size={16} />
            </ActionIcon>
            <Box flex={1} miw={0}>
              <DateField aria-label="Day" value={day} onChange={pickDay} minDate={floorDay} maxDate={today} size="sm" w={{ base: '100%', sm: 200 }} />
            </Box>
            <ActionIcon
              variant="default"
              size="lg"
              aria-label="Next day"
              disabled={!nav.canGoForward}
              onClick={() => pickDay(shiftDay(day, 1, zone))}
            >
              <IconChevronRight size={16} />
            </ActionIcon>
          </Group>
        )}

        {effectiveMode === 'range' && (
          <Group gap="sm" align="flex-end" wrap="wrap" w={{ base: '100%', sm: 'auto' }}>
            <Group gap="xs" align="flex-end" wrap="nowrap" w={{ base: '100%', sm: 'auto' }}>
              <Box flex={1} miw={0}>
                <DateField
                  label="From"
                  value={range.startDate}
                  onChange={(d) => d && commitRange({ ...range, startDate: d })}
                  minDate={floorDay}
                  maxDate={today}
                  size="sm"
                  w={{ base: '100%', sm: 180 }}
                />
              </Box>
              <Select
                aria-label="From hour"
                data={hourOptions('start')}
                value={String(range.startHour)}
                onChange={(v) => v !== null && commitRange({ ...range, startHour: Number(v) })}
                allowDeselect={false}
                size="sm"
                w={110}
              />
            </Group>
            <Group gap="xs" align="flex-end" wrap="nowrap" w={{ base: '100%', sm: 'auto' }}>
              <Box flex={1} miw={0}>
                <DateField
                  label="To"
                  value={range.endDate}
                  onChange={(d) => d && commitRange({ ...range, endDate: d })}
                  minDate={range.startDate}
                  maxDate={today}
                  size="sm"
                  w={{ base: '100%', sm: 180 }}
                />
              </Box>
              <Select
                aria-label="To hour"
                data={hourOptions('end')}
                value={String(range.endHour)}
                onChange={(v) => v !== null && commitRange({ ...range, endHour: Number(v) })}
                allowDeselect={false}
                size="sm"
                w={110}
              />
              {!rangeIsWholeDays(range) && (
                <Button variant="subtle" size="sm" onClick={() => commitRange({ ...range, startHour: 0, endHour: 24 })}>
                  Whole days
                </Button>
              )}
            </Group>
          </Group>
        )}
      </Group>

      {showBroadcasts && broadcasts.continuous && (
        <Text size="xs" c="dimmed">
          {CONTINUOUS_STREAM_COPY}
        </Text>
      )}

      {effectiveMode === 'range' && (
        <Text size="xs" c={rangeError ? 'red' : 'dimmed'}>
          {rangeError ?? 'Whole days unless you set hours.'}
        </Text>
      )}

      <Group gap="sm" align="center" wrap="wrap">
        <Text size="sm" fw={500}>
          {effectiveWindowLabel(value, zone)}
        </Text>
        <Button
          variant="subtle"
          size="compact-xs"
          leftSection={copied ? <IconCheck size={14} /> : <IconLink size={14} />}
          onClick={() => void copyLink()}
        >
          {copied ? 'Copied' : 'Copy report link'}
        </Button>
        {!isToday && (
          <Anchor component="button" type="button" size="xs" onClick={() => emit(null)}>
            Back to today
          </Anchor>
        )}
        {pageRange && !pageRangeIsCurrent && (
          <Anchor component="button" type="button" size="xs" onClick={() => emit(pageRange)}>
            Use the Content tab&apos;s range ({pageRangeLabel})
          </Anchor>
        )}
      </Group>
    </Stack>
  );
}
